import { Table } from 'antd'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BackgroundBox from '../../../../components/custom_components/BackgroundBox'
import PerButtonCircle from '../../../../components/custom_components/ButtonCircle'
import PerText from '../../../../components/custom_components/Text'
import { useAuth } from '../../../../context/auth-context'
import { NationalContractReferenceLevelDto, NationalContractReferenceWithTypeDto } from '../../../../types'
import { useAmountAndCurrency } from '../../../../shared/useAmountAndCurrency'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { HrSupportNationalContractReferenceEditModal } from './HrSupportNationalContractReferenceEditModal'

interface Props {
  reference: NationalContractReferenceWithTypeDto
}

export const HrSupportNationalContractReferenceCard: FC<Props> = ({ reference }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)

  const columns = useMemo(
    () => [
      {
        key: 'level',
        title: t('companySettingsView.nationalContractReferenceTab.table.columns.level'),
        className: 'textAlign-left',
        width: '40%',
        sorter: (a: NationalContractReferenceLevelDto, b: NationalContractReferenceLevelDto) => a.displayOrder - b.displayOrder,
        render: (row: NationalContractReferenceLevelDto) => (
          <PerText fontSize="xs">{t(`employee.nationalContract.level.${row.level}`)}</PerText>
        ),
      },
      {
        key: 'minSalary',
        title: t('companySettingsView.nationalContractReferenceTab.table.columns.minSalary'),
        className: 'textAlign-right',
        width: '60%',
        sorter: (a: NationalContractReferenceLevelDto, b: NationalContractReferenceLevelDto) => a.minSalary - b.minSalary,
        render: (row: NationalContractReferenceLevelDto) => (
          <PerText color="secondary" fontSize="xs">
            {formatCurrency(row.minSalary, 'EUR', true)}
          </PerText>
        ),
      },
    ],
    [formatCurrency, t],
  )

  const dataSource = useMemo(() => [...reference.levels].sort((a, b) => b.displayOrder - a.displayOrder), [reference.levels])

  return (
    <BackgroundBox r color="white" noPadding noShadow>
      <div className="per-hr-support-national-contract-reference__card">
        <PerFlexRow justify="space-between" align="middle" width="100%" mb="xxs">
          <PerText fontSize="s" fontWeight="bold">
            {t(`employee.nationalContract.type.${reference.type}`)}
          </PerText>
          <PerButtonCircle
            icon="edit_fill"
            color="primary"
            variant="pastel"
            tooltip={t('tooltip.edit')}
            onClick={() => setIsEditModalVisible(true)}
          />
        </PerFlexRow>

        <PerFlexRow width="100%">
          <Table
            className="fullWidth small"
            rowKey={(row) => `${reference.type}-${row.level}`}
            columns={columns}
            dataSource={dataSource}
            pagination={false}
          />
        </PerFlexRow>
      </div>

      <HrSupportNationalContractReferenceEditModal
        visible={isEditModalVisible}
        reference={reference}
        onClose={() => setIsEditModalVisible(false)}
      />
    </BackgroundBox>
  )
}
